<template>
  <div class="dynamic-form-editor">
    <h4 class="font-weight-bold">
      {{ editorTitle }}
      <b-button class="ml-2" variant="secondary" @click="exportSubjects">
        匯出 JSON
      </b-button>
      <b-button class="ml-2" variant="secondary" @click="importSubjects">
        匯入 JSON
      </b-button>
    </h4>

    <section class="dynamic-form-editor__subjects">
      <VueDraggable
        handle=".subject-layout-drag-handler"
        :list="newSubjects"
        :disabled="isReadOnly"
      >
        <b-card v-for="(newSubject, index) in newSubjects" class="mt-3" :key="`${newSubjects[index]._id}_${rerenderingCount}_${validateSubject}`">
          <SubjectLayout
            :ref="'subjectLayoutRef+'+newSubjects[index]._id"
            :id="`subject-${newSubjects[index]._id}`"
            :index="index"
            :type="newSubject.type"
            :title.sync="newSubjects[index].title"
            :memo.sync="newSubjects[index].memo"
            :assign-to-customer-data.sync="newSubjects[index].assign_to_customer_data"
            :save-to-customer-data.sync="newSubjects[index].save_to_customer_data"
            :placeholder.sync="newSubjects[index].placeholder"
            :required.sync="newSubjects[index].required"
            :validation-rules.sync="newSubjects[index].validation_rules"
            :dashboardOnly.sync="newSubjects[index].dashboard_only"
            :mainDisplay.sync="newSubjects[index].main_display"
            :notRepeatable.sync="newSubjects[index].not_repeatable"
            :move-up-is-disabled="index == 0"
            :move-down-is-disabled="index == newSubjects.length - 1"
            :has-placeholder="['text', 'textarea', 'select', 'datetime', 'collection_carousel'].includes(newSubject.type)"
            :expanded.sync="expandedStateForSubjects[newSubjects[index]._id]"
            :form-type="formType"
            :listHidden.sync="newSubjects[index].list_hidden"
            :readonly="isReadOnly"
            :title-is-editable="false === subjectIsFixed(newSubjects[index])"
            :expand-button-is-visible="false === subjectIsFixed(newSubjects[index])"
            :is-deletable="false === subjectIsFixed(newSubjects[index])"
            :page.sync="newSubjects[index].page_id"
            :page-options="pages"
            :section.sync="newSubjects[index].section_id"
            :section-options="sections"
            :visible-conditions.sync="newSubjects[index].visible_when_any"
            :field-options-for-visible-conditions="inputSubjects"
            :validate="validateSubject"
            :validation-message="`第${index+1}題 ${validationMessage}`"
            @top="moveToTop(index)"
            @up="moveUp(index)"
            @down="moveDown(index)"
            @bottom="moveToBottom(index)"
            @remove="removeNewSubject(index)"
          >
            <template #default>
              <!-- radio, checkbox, select start -->
              <b-form-group v-if="['radio', 'checkbox', 'select'].includes(newSubject.type)">
                <div class="option-list-header">
                  <div class="option-list-header__title" style="width: 13rem; padding-left: 2rem">選項文字</div>
                  <div class="option-list-header__title ml-2" style="width: 8rem">管理</div>
                  <div class="option-list-header__title ml-2" style="width: 5rem">前台隱藏</div>
                </div>
                <div class="mt-3">
                  <VueDraggable
                    handle=".option-drag-handler"
                    :list="newSubjects[index].options"
                    :disabled="isReadOnly"
                  >
                    <div v-for="(option, optionIndex) in newSubjects[index].options" class="mb-3 d-flex align-items-center" :key="index + '-opt-' + optionIndex">
                      <div class="flex-grow-0 flex-shrink-0 d-flex align-items-center" style="width: 13rem">
                        <i class="fa fa-bars mr-3 option-drag-handler"></i>
                        <b-form-input
                          placeholder="請輸入選項"
                          v-model="newSubjects[index].options[optionIndex].text"
                          :disabled="isReadOnly"
                          @change="syncOptionValue(index, optionIndex)"
                          @compositionstart="compositionStart"
                          @compositionend="compositionEnd(index, optionIndex)"
                          @keydown.enter="event => handleKeyDown({event, subjectIndex: index, optionIndex, id: newSubject._id})"
                          @paste="e => handlePaste(e, index, optionIndex)"
                          :ref="`${newSubject._id}_${optionIndex}`">
                        </b-form-input>
                      </div>
                      <div class="flex-grow-0 flex-shrink-0 ml-2" style="width: 8rem" v-if="!isReadOnly">
                        <b-button class=" p-1 d-inline-flex align-items-center" variant="inverse-success" @click="createOption(optionIndex,newSubject._id)">
                          <span class="mdi mdi-18px mdi-playlist-plus"></span>
                          增加
                        </b-button>
                        <b-button class="ml-2 p-1 d-inline-flex align-items-center" variant="inverse-danger" @click="removeOption(index, optionIndex)" :disabled="isOnlyOneOption(index)">
                          <span class="mdi mdi-18px mdi-playlist-remove"></span>
                          移除
                        </b-button>
                      </div>
                      <div class="flex-grow-0 flex-shrink-0 ml-2" style="width: 5rem" v-if="!isReadOnly">
                        <b-form-checkbox v-model="newSubjects[index].options[optionIndex].staff_only"></b-form-checkbox>
                      </div>
                    </div>
                  </VueDraggable>
                </div>
              </b-form-group>
              <!-- radio, checkbox, select end -->

              <!-- datetime start -->
              <template v-if="newSubject.type === 'datetime'">
                <b-form-group label="格式 *" label-size="sm" :disabled="isReadOnly">
                  <b-form-radio-group
                    class="input-group"
                    v-model="newSubjects[index].format"
                    :options="[{ text: '日期 + 時間', value: 'datetime' }, { text: '日期', value: 'date' }, { text: '時間', value: 'time' }]"
                    @change="handleFieldChange(newSubjects[index], 'format')"
                  ></b-form-radio-group>
                </b-form-group>

                <b-overlay v-if="isInclude('date', newSubjects[index].format)" class="mt-2">
                  <b-form-group label="日期格式 *" label-size="sm" :disabled="isReadOnly" clsss="mt-2">
                    <!-- <b-form-radio-group
                      v-model="newSubjects[index].date_condition"
                      @change="changeDateCondition($event, index)"
                      :options="[
                        { text: '自行選擇日期', value: 'unlimited' },
                        { text: '特定日期', value: 'limited' , disabled: bookingPresetType == 'wizard' && startAtFieldId == newSubject._id }]"
                    >
                    </b-form-radio-group> -->

                    <b-form-radio-group
                      v-model="newSubjects[index].date_condition"
                      @change="changeDateCondition($event, index)"                      
                    >
                    <b-form-radio value="unlimited">
                      自行選擇日期
                    </b-form-radio>
                    <span :id="`limited-date-wrap-${newSubject._id}`" class="d-inline-block">
                      <b-form-radio
                        value="limited"
                        :disabled="bookingPresetType === 'wizard' && startAtFieldId === newSubject._id"
                      >
                        特定日期
                      </b-form-radio>
                    </span>
                    <b-tooltip
                      v-if="bookingPresetType === 'wizard' && startAtFieldId === newSubject._id"
                      :target="`limited-date-wrap-${newSubject._id}`"
                      triggers="hover focus"
                    >
                    精靈模式尚未支援特定日期
                    </b-tooltip>
                    </b-form-radio-group>
                  </b-form-group>
                  <b-form-group label="日期呈現樣式" label-size="sm" disabled clsss="mt-2">
                    <b-form-radio-group
                      v-model="newSubjects[index].date_style"
                      :options="[{ text: '日曆下拉', value: 'calendar_style' }, { text: '日期下拉', value: 'select_style' }]"
                    >
                    </b-form-radio-group>
                  </b-form-group>
                  
                </b-overlay>

                <b-overlay v-if="isInclude('time', newSubjects[index].format)" class="mt-2">
                  <b-form-group label="時間格式 *" label-size="sm" :disabled="isReadOnly" class="mt-2">
                    <b-form-radio-group
                      class="input-group"
                      v-model="newSubjects[index].time_condition"
                      :options="[
                        { text: '自行選擇時間', value: 'unlimited' },
                        { text: '連續時間', value: 'limited' },
                        { text: '特定時間', value: 'specified' }
                      ]"
                      @change="handleTimeConditionChange($event, index)"
                    ></b-form-radio-group>
                  </b-form-group>
                  <b-form-group label="時間呈現樣式" label-size="sm" :disabled="isReadOnly" clsss="mt-2">
                    <b-form-radio-group
                      v-model="newSubjects[index].time_style"
                      :options="[
                        { text: '時間下拉', value: 'time_select_style', disabled: true },
                        { text: '自訂下拉', value: 'select_style', disabled: newSubjects[index].time_condition === 'unlimited' },
                        { text: '內嵌時間', value: 'button_style', disabled: newSubjects[index].time_condition === 'unlimited' }
                      ]"
                    >
                    </b-form-radio-group>
                  </b-form-group>


                  <b-form-group v-if="isInclude('datetime', newSubjects[index].format)" label="時段設定" label-size="sm" :disabled="isReadOnly" clsss="mt-2">
                    <b-form-radio-group
                      v-model="newSubjects[index].daily_time"
                      :options="[
                        { text: '每日固定時段', value: 'daily_fixed_time' , disabled: newSubjects[index].time_condition !== 'specified'},
                        { text: '依日期調整時段', value: 'daily_flexible_time', disabled: newSubjects[index].time_condition !== 'specified'},
                      ]"
                      @change="handleDailyTimeChange(newSubjects[index].daily_time, index)"
                    >
                    </b-form-radio-group>
                  </b-form-group>
                </b-overlay>


                <template v-if="isInclude('date', newSubjects[index].format)">
                    <b-form-group label="" label-size="sm" v-if="newSubjects[index].date_condition == 'limited'">
                      <div class="mt-3">
                        <VueDraggable
                          handle=".option-drag-handler"
                          :list="newSubjects[index].options"
                          :disabled="isReadOnly"
                        >
                          <b-card v-for="(option, optionIndex) in newSubjects[index].options" class="mb-3 rounded rounded-2 shadow-none" bg-variant="light" :key="index + '-opt-' + optionIndex" no-body>
                            
                            <template #header>
                              <div class="d-flex align-items-center">
                                <div class="d-flex align-items-center flex-fill">
                                  <i class="fa fa-bars mr-3 option-drag-handler"></i>
                                  <!-- <b-form-input
                                    type="date"
                                    placeholder="請輸入選項"
                                    v-model="newSubjects[index].options[optionIndex].text"
                                    :disabled="isReadOnly"
                                    @change="syncOptionValue(index, optionIndex)"
                                    @compositionstart="compositionStart"
                                    @compositionend="compositionEnd(index, optionIndex)"
                                    @keydown.enter="event => handleKeyDown({event, subjectIndex: index, optionIndex, id: newSubject._id})"
                                    @paste="e => handlePaste(e, index, optionIndex)"
                                    :ref="`${newSubject._id}_${optionIndex}`">
                                  </b-form-input> -->


                                  <AppDatePicker
                                    :ref="`${newSubject._id}_${optionIndex}`"
                                    v-model="newSubjects[index].options[optionIndex].value"
                                    :disabled="isReadOnly"
                                  />
                                  <b-form-input
                                    v-model="newSubjects[index].options[optionIndex].text"
                                    placeholder="請填入顯示文字"
                                    :disabled="isReadOnly"
                                    style="width: 200px"
                                    class="ml-2"
                                  />
                                </div>
                                <div class="flex-shrink-0" v-if="!isReadOnly">
                                  <b-button class="ml-2 p-1 d-inline-flex align-items-center" variant="inverse-success" @click="createOption(optionIndex,newSubject._id)">
                                    <span class="mdi mdi-18px mdi-playlist-plus"></span>
                                    增加
                                  </b-button>
                                  <b-button class="ml-2 p-1 d-inline-flex align-items-center" variant="inverse-danger" @click="removeOption(index, optionIndex)" :disabled="isOnlyOneOption(index)">
                                    <span class="mdi mdi-18px mdi-playlist-remove"></span>
                                    移除
                                  </b-button>
                                </div>
                              </div>
                            </template>

                            <div v-if="newSubjects[index].daily_time == 'daily_flexible_time'" class="pb-0 pt-3 pr-3 pl-5">
                                <VueDraggable
                                  handle=".option-drag-handler"
                                  :list="newSubjects[index].options[optionIndex].time_options"
                                  :disabled="isReadOnly"
                                >
                                  <div v-for="(option, timeOptionIndex) in newSubjects[index].options[optionIndex].time_options" class="mb-3 d-flex align-items-center justify-content-between" :key="index + '-daily-time-opt-' + optionIndex + '-time-opt-' + timeOptionIndex">
                                    <div class="d-flex align-items-center justify-content-start">
                                      <i class="fa fa-bars mr-3 option-drag-handler"></i>
                                      <AppTimePicker
                                        v-model="newSubjects[index].options[optionIndex].time_options[timeOptionIndex].value"
                                        :disabled="isReadOnly"
                                      />
                                      <b-form-input
                                        v-model="newSubjects[index].options[optionIndex].time_options[timeOptionIndex].text"
                                        placeholder="請填入顯示文字"
                                        :disabled="isReadOnly"
                                        style="width: 200px"
                                        class="ml-2"
                                      />
                                    </div>
                                    <div class="d-flex" v-if="!isReadOnly">
                                      <b-button class="ml-2 p-1 d-inline-flex align-items-center" variant="inverse-success" @click="createDailyTimeOption(index, optionIndex, timeOptionIndex)">
                                        <span class="mdi mdi-18px mdi-playlist-plus"></span>
                                        增加
                                      </b-button>
                                      <b-button class="ml-2 p-1 d-inline-flex align-items-center" variant="inverse-danger" @click="removeDailyTimeOption(index, optionIndex, timeOptionIndex)" :disabled="newSubjects[index].options[optionIndex].time_options.length === 1">
                                        <span class="mdi mdi-18px mdi-playlist-remove"></span>
                                        移除
                                      </b-button>
                                    </div>
                                  </div>
                                </VueDraggable>
                            </div>
                          </b-card>
                        </VueDraggable>
                      </div>
                    </b-form-group>
                  </template>

                  <template v-if="isInclude('time', newSubjects[index].format) && newSubjects[index].daily_time == 'daily_fixed_time'">
                    <!-- 連續時間 start -->
                    <div v-if="newSubjects[index].time_condition == 'limited'" clsss="mt-2">
                      <b-form-group label="開始時間 *" label-size="sm" :disabled="isReadOnly" clsss="mt-2">
                        <AppTimePicker
                          v-model="newSubjects[index].start_time"
                        />
                      </b-form-group>
                      <b-form-group label="結束時間 *" label-size="sm" :disabled="isReadOnly" clsss="mt-2">
                        <AppTimePicker
                          v-model="newSubjects[index].end_time"
                        />
                      </b-form-group>
                      <b-form-group label="每個時間區段單位 *" label-size="sm" :disabled="isReadOnly" clsss="mt-2">
                        <b-form-input
                          v-model="newSubjects[index].slot_minutes"
                        ></b-form-input>
                      </b-form-group>
                    </div>
                    <!-- 連續時間 end -->

                    <!-- 特定時間 start -->
                    <b-form-group label="" label-size="sm" v-if="newSubjects[index].time_condition == 'specified'">
                      <div class="mt-3">
                        <VueDraggable
                          handle=".option-drag-handler"
                          :list="newSubjects[index].time_options"
                          :disabled="isReadOnly"
                        >
                          <div v-for="(option, optionIndex) in newSubjects[index].time_options" class="mb-3 d-flex align-items-center" :key="index + '-time-opt-' + optionIndex">
                            <div class="flex-grow-1 flex-shrink-1 d-flex align-items-center">
                              <i class="fa fa-bars mr-3 option-drag-handler"></i>
                              <AppTimePicker
                                v-model="newSubjects[index].time_options[optionIndex].value"
                                :disabled="isReadOnly"
                              />
                              <b-form-input
                                v-model="newSubjects[index].time_options[optionIndex].text"
                                placeholder="請填入顯示文字"
                                :disabled="isReadOnly"
                                style="width: 200px"
                                class="ml-2"
                              />
                            </div>
                            <div class="flex-shrink-0" v-if="!isReadOnly">
                              <b-button class="ml-2 p-1 d-inline-flex align-items-center" variant="inverse-success" @click="createTimeOption(index, optionIndex)">
                                <span class="mdi mdi-18px mdi-playlist-plus"></span>
                                增加
                              </b-button>
                              <b-button class="ml-2 p-1 d-inline-flex align-items-center" variant="inverse-danger" @click="removeTimeOption(index, optionIndex)" :disabled="newSubjects[index].time_options.length === 1">
                                <span class="mdi mdi-18px mdi-playlist-remove"></span>
                                移除
                              </b-button>
                            </div>
                          </div>
                        </VueDraggable>
                      </div>
                    </b-form-group>
                    <!-- 特定時間 end -->
                  </template>
               
              </template>
              <!-- datetime end -->

              <!-- collection select start -->
              <template v-if="newSubject.type === 'collection_select'">
                <b-form-group label="資料集關聯 *" label-size="sm" :disabled="isReadOnly">
                  <b-form-select
                    v-model="newSubjects[index].collection_id"
                    :options="collectionOptions"
                    v-on:change="handleCollectionSelectChange($event, newSubjects[index]._id)"
                  >
                    <template #first>
                      <b-form-select-option :value="null">請選擇資料集</b-form-select-option>
                    </template>
                  </b-form-select>
                </b-form-group>
                <b-form-group label="呈現方式" label-size="sm">
                  <b-form-radio-group
                    v-model="newSubjects[index].collection_presentation"
                    :options="[
                      { text: '下拉選單', value: 'select' },
                      { text: '單選題', value: 'radio' },
                      { text: '多選題', value: 'checkbox' },
                    ]"
                  ></b-form-radio-group>
                </b-form-group>
                <b-form-group label-size="sm" :disabled="isReadOnly">
                  <template #label>
                    分店關聯
                    <i
                      class="fa fa-question-circle ml-2 text-muted"
                      :id="`branch-field-tooltip-select-${index}`"
                      style="font-size: 0.9rem;"
                    ></i>
                  </template>
                  <b-form-select
                    v-model="newSubjects[index].branch_field_id"
                    :options="branchFieldOptions"
                  ></b-form-select>
                  <b-tooltip :target="`branch-field-tooltip-select-${index}`" triggers="hover" placement="right">
                    若資料集需與分店連動，請選擇分店欄位；若無需連動，則無須設定
                  </b-tooltip>
                </b-form-group>
              </template>
              <!-- collection select end -->

              <!-- collection carousel start -->
              <template v-if="newSubject.type === 'collection_carousel'">
                <b-form-group label="資料集關聯 *" label-size="sm" :disabled="isReadOnly">
                  <b-form-select
                    v-model="newSubjects[index].collection_id"
                    :options="collectionOptions"
                    @input="(collectionId) => handleCollectionCarouselChange(collectionId, newSubjects[index])"
                  >
                    <template #first>
                      <b-form-select-option :value="null">請選擇資料集</b-form-select-option>
                    </template>
                  </b-form-select>
                </b-form-group>
                <b-overlay :show="isFetchCollectionFields[newSubjects[index].collection_id] === true">
                  <b-form-group label="標題欄位" label-size="sm" :disabled="isReadOnly">
                    <b-form-select
                      v-model="newSubjects[index].collection_title"
                      :options="deepGet(collectionFieldOptions, newSubjects[index].collection_id, [])"
                      @input="(mappingFieldId) => handleCollectionCarouselFieldMappingChange(mappingFieldId, newSubjects[index])"
                    >
                      <template #first>
                        <b-form-select-option :value="null">請設定欄位對應</b-form-select-option>
                      </template>
                    </b-form-select>
                  </b-form-group>
                  <b-form-group label="圖片欄位" label-size="sm" :disabled="isReadOnly">
                    <b-form-select
                      v-model="newSubjects[index].collection_image"
                      :options="deepGet(collectionFieldOptions, newSubjects[index].collection_id, [])"
                      @input="(mappingFieldId) => handleCollectionCarouselFieldMappingChange(mappingFieldId, newSubjects[index])"
                    >
                      <template #first>
                        <b-form-select-option :value="null">請設定欄位對應</b-form-select-option>
                      </template>
                    </b-form-select>
                  </b-form-group>
                  <b-form-group label="說明欄位" label-size="sm" :disabled="isReadOnly">
                    <b-form-select
                      v-model="newSubjects[index].collection_description"
                      :options="deepGet(collectionFieldOptions, newSubjects[index].collection_id, [])"
                      @input="(mappingFieldId) => handleCollectionCarouselFieldMappingChange(mappingFieldId, newSubjects[index])"
                    >
                      <template #first>
                        <b-form-select-option :value="null">請設定欄位對應</b-form-select-option>
                      </template>
                    </b-form-select>
                  </b-form-group>
                  <b-form-group label-size="sm" :disabled="isReadOnly">
                    <template #label>
                      分店關聯
                      <i
                        class="fa fa-question-circle ml-2 text-muted"
                        :id="`branch-field-tooltip-carousel-${index}`"
                        style="font-size: 0.9rem;"
                      ></i>
                    </template>
                    <b-form-select
                      v-model="newSubjects[index].branch_field_id"
                      :options="branchFieldOptions"
                    ></b-form-select>
                    <b-tooltip :target="`branch-field-tooltip-carousel-${index}`" triggers="hover" placement="right">
                      若資料集需與分店連動，請選擇分店欄位；若無需連動，則無須設定
                    </b-tooltip>
                  </b-form-group>
                </b-overlay>
              </template>
              <!-- collection carousel end -->

              <!-- shop selection start -->
              <template v-if="newSubject.type === 'shop_selection'">
                <b-form-group label="分店顯示類型 *" label-size="sm">
                  <b-form-radio-group
                    class="input-group"
                    v-model="newSubjects[index].branch_population"
                    :options="[{ text: '所有分店', value: 'all' }, { text: '自訂分店', value: 'subset' }]"
                    @input="handleShopSelectionPopulationChange(newSubjects[index])"
                  ></b-form-radio-group>
                </b-form-group>

                <template v-if="deepGet(newSubjects[index], 'branch_population', 'all') === 'all'">
                  <b-form-group label="分店下拉選單來源" label-size="sm">
                    <BranchProviderEditor
                      :ref="`branch-provider-editor-${newSubjects[index]._id}`"
                      :showBranchCode.sync="newSubjects[index].show_branch_code"
                      v-model="newSubjects[index].branch_provider"
                      @input="handleShopSelectionProviderChange(newSubjects[index])"
                    />
                  </b-form-group>
                </template>
                <template v-else-if="deepGet(newSubjects[index], 'branch_population', 'all') === 'subset'">
                  <BranchSubsetEditor
                    class="mb-3"
                    :showBranchCode.sync="newSubjects[index].show_branch_code"
                    v-model="newSubjects[index].branch_ids"
                    @input="handleBranchSubsetChange(newSubjects[index])"
                  />
                  <b-form-group label-size="sm">
                    <b-form-checkbox
                      v-model="newSubjects[index].show_parent_branch_options"
                      :value="true"
                      :unchecked-value="false"
                      @input="value => handleShowParentBranchesChange(value, newSubjects[index])"
                    >
                      顯示上層分店
                    </b-form-checkbox>

                    <div
                      class="shop-selection-parent-branch-options mt-3 ml-4"
                      :class="{ 'shop-selection-parent-branch-options--disabled': !newSubjects[index].show_parent_branch_options }"
                    >
                      <b-form-radio
                        v-model="newSubjects[index].parent_branch_population"
                        value="parent"
                        :disabled="!newSubjects[index].show_parent_branch_options"
                        @input="handleParentBranchPopulationChange(newSubjects[index])"
                      >
                        只顯示上一層
                      </b-form-radio>
                      <b-form-radio
                        class="mt-2"
                        v-model="newSubjects[index].parent_branch_population"
                        value="specific"
                        :disabled="!newSubjects[index].show_parent_branch_options"
                        @input="handleParentBranchPopulationChange(newSubjects[index])"
                      >
                        從指定分店向下展開
                      </b-form-radio>
                      <div class="shop-selection-parent-branch-provider mt-2 ml-4">
                        <BranchProviderEditor
                          :ref="`parent-branch-provider-editor-${newSubjects[index]._id}`"
                          button-text="選擇分店"
                          hide-show-branch-code-toggle
                          require-specific-branch
                          :showBranchCode="newSubjects[index].show_branch_code"
                          v-model="newSubjects[index].parent_branch_provider"
                          :class="{ 'shop-selection-parent-branch-provider--disabled': !parentBranchProviderIsEnabled(newSubjects[index]) }"
                          @mounted="loadSelectedParentBranch(newSubjects[index])"
                          @input="handleParentBranchProviderChange(newSubjects[index])"
                        />
                      </div>
                    </div>
                  </b-form-group>
                </template>

                <b-form-group label="分店下拉選單文字" label-size="sm">
                  <b-form-input
                    type="text"
                    v-model="newSubjects[index].default_select_text"
                    style="width: 200px"
                  ></b-form-input>
                </b-form-group>

                <b-form-group label="是否顯示分店代號" label-size="sm">
                  <b-form-checkbox
                    v-model="newSubjects[index].show_branch_code"
                    :value="true"
                    :unchecked-value="false"
                  ></b-form-checkbox>
                </b-form-group>
              </template>
              <!-- shop selection end -->
            </template>

            <template #preview>
              <b-form-group
                class="mb-0"
                :class="`form-group-${newSubject.type}`"
                :label="newSubjects[index].title"
                :description="newSubjects[index].memo"
              >
                <template v-if="newSubject.type === 'text'">
                  <b-form-input
                    :placeholder="newSubjects[index].placeholder">
                  </b-form-input>
                </template>
                <template v-if="newSubject.type === 'textarea'">
                  <b-form-textarea
                    :placeholder="newSubjects[index].placeholder"
                    :rows="newSubjects[index].rows"
                  >
                  </b-form-textarea>
                </template>

                <template v-if="newSubject.type === 'select'">
                  <b-form-select
                    :value="null"
                    :options="previewOptions(index)"
                  >
                  </b-form-select>
                </template>

                <template v-if="newSubject.type === 'radio'">
                  <b-form-radio-group class="input-group">
                    <b-form-radio
                      v-for="option in previewOptions(index)"
                      :key="option.value"
                      :value="option.value"
                      :disabled="option.disabled"
                    >
                      <template v-if="option.staff_only">
                        <div :id="`radio-${index}-${option.value}`">
                          {{ option.text }}
                          <span class="fa fa-question-circle"></span>
                        </div>
                        <b-tooltip :target="`radio-${index}-${option.value}`" placement="right">前台不會顯示這個選項</b-tooltip>
                      </template>
                      <template v-else>
                        {{ option.text }}
                      </template>
                    </b-form-radio>
                  </b-form-radio-group>
                </template>

                <template v-if="newSubject.type === 'checkbox'">
                  <b-form-checkbox-group
                    class="input-group"
                    :stacked="newSubjects[index].is_vertical"
                  >
                    <b-form-checkbox
                      v-for="option in previewOptions(index)"
                      :key="option.value"
                      :value="option.value"
                      :disabled="option.disabled"
                    >
                      <template v-if="option.staff_only">
                        <div :id="`checkbox-${index}-${option.value}`">
                          {{ option.text }}
                          <span class="fa fa-question-circle"></span>
                        </div>
                        <b-tooltip :target="`checkbox-${index}-${option.value}`" placement="right">前台不會顯示這個選項</b-tooltip>
                      </template>
                      <template v-else>
                        {{ option.text }}
                      </template>
                    </b-form-checkbox>
                  </b-form-checkbox-group>
                </template>

                <template v-if="newSubject.type === 'datetime'">
                  <template v-if="newSubjects[index].format === 'datetime'">
                    <DynamicFormDateTimePicker
                      :placeholder-for-date="newSubjects[index].placeholder"
                      :placeholder-for-time="newSubjects[index].placeholder"
                      :daily-time="newSubjects[index].daily_time"
                      :time-condition="newSubjects[index].time_condition"
                      :start-time="newSubjects[index].start_time"
                      :end-time="newSubjects[index].end_time"
                      :slot-minutes="newSubjects[index].slot_minutes"
                      :time-style="newSubjects[index].time_style"
                      :date-condition="newSubjects[index].date_condition"
                      :date-time-options="newSubjects[index].options"
                      :custom-time-options="newSubjects[index].time_options || []"
                    ></DynamicFormDateTimePicker>
                  </template>
                  <template v-if="newSubjects[index].format === 'date'">
                    <DynamicFormDatePicker
                      :placeholder="newSubjects[index].placeholder"
                      :date-options="newSubjects[index].options"
                      :date-type="newSubjects[index].date_condition"
                    ></DynamicFormDatePicker>
                  </template>
                  <template v-if="newSubjects[index].format === 'time'">
                    <DynamicFormTimePicker
                      :placeholder="newSubjects[index].placeholder"
                      :style-type="newSubjects[index].time_condition"
                      :start-time="newSubjects[index].start_time"
                      :end-time="newSubjects[index].end_time"
                      :slot-minutes="newSubjects[index].slot_minutes"
                      :time-style="newSubjects[index].time_style"
                      :custom-time-options="newSubjects[index].time_options || []"
                    ></DynamicFormTimePicker>
                  </template>
                </template>

                <template v-if="newSubject.type === 'uploader'">
                  <AppFileUploader />
                </template>

                <template v-if="newSubject.type === 'collection_select'">
                  <template v-if="newSubjects[index].collection_presentation === 'select'">
                    <b-form-select
                      :value="null"
                      :options="previewCollectionItemOptions[newSubjects[index]._id]"
                    ></b-form-select>
                  </template>
                  <template v-if="newSubjects[index].collection_presentation === 'radio'">
                    <b-form-radio-group class="input-group">
                      <b-form-radio
                        v-for="option in previewCollectionItemOptions[newSubjects[index]._id]"
                        :key="option.value"
                        :value="option.value"
                        :disabled="option.disabled"
                      >
                        {{ option.text }}
                      </b-form-radio>
                    </b-form-radio-group>
                  </template>
                  <template v-if="newSubjects[index].collection_presentation === 'checkbox'">
                    <b-form-checkbox-group class="input-group">
                      <b-form-checkbox
                        v-for="option in previewCollectionItemOptions[newSubjects[index]._id]"
                        :key="option.value"
                        :value="option.value"
                        :disabled="option.disabled"
                      >
                        {{ option.text }}
                      </b-form-checkbox>
                    </b-form-checkbox-group>
                  </template>
                </template>

                <template v-if="newSubject.type === 'collection_carousel'">
                  <CarouselSelection
                    :ref="`carousel-preview-${newSubjects[index]._id}`"
                    :value="null"
                    :placeholder="newSubjects[index].placeholder"
                    :candidate-fetcher="() => fetchCollectionItemForCarousel(newSubjects[index])"
                  ></CarouselSelection>
                </template>

                <template v-if="newSubject.type === 'shop_selection'">
                  <ShopSelection
                    v-if="deepGet(newSubjects[index], 'branch_population', 'all') === 'all'"
                    :ref="`shop-selection-${newSubjects[index]._id}`"
                    :branch-provider="newSubjects[index].branch_provider || ''"
                    :show-branch-code="newSubjects[index].show_branch_code || false"
                    :placeholder="newSubjects[index].default_select_text || ''"
                    api-source="admin"
                  />
                  <ShopSubsetSelection
                    v-if="deepGet(newSubjects[index], 'branch_population', 'all') === 'subset'"
                    :ref="`shop-subset-selection-${newSubjects[index]._id}`"
                    :branch-ids="newSubjects[index].branch_ids || ''"
                    :show-parent-branches="newSubjects[index].show_parent_branch_options"
                    :parent-branch-population="newSubjects[index].parent_branch_population || 'parent'"
                    :parent-branch-provider="newSubjects[index].parent_branch_provider || null"
                    :show-branch-code="newSubjects[index].show_branch_code || false"
                    :placeholder="newSubjects[index].default_select_text || ''"
                    api-source="admin"
                  />
                </template>
              </b-form-group>
            </template>
          </SubjectLayout>
        </b-card>
      </VueDraggable>
    </section>

    <div v-if="!isReadOnly" class="subject-type-menu mt-3">
      <span class="subject-type-menu__icon mdi mdi-24px mdi-plus"></span>
      <div class="subject-type-menu__button-wrapper">
        <b-button class="subject-type-menu__create-button" variant="inverse-primary" @click="createNewSubject('text')">
          <i class="mdi mdi-18px" :class="subjectTypeIcon('text')"></i>
          單行文字
        </b-button>
        <b-button class="subject-type-menu__create-button" variant="inverse-primary" @click="createNewSubject('textarea')">
          <i class="mdi mdi-18px" :class="subjectTypeIcon('textarea')"></i>
          多行文字
        </b-button>
        <b-button class="subject-type-menu__create-button" variant="inverse-primary" @click="createNewSubject('radio')">
          <i class="mdi mdi-18px" :class="subjectTypeIcon('radio')"></i>
          單選題
        </b-button>
        <b-button class="subject-type-menu__create-button" variant="inverse-primary" @click="createNewSubject('checkbox')">
          <i class="mdi mdi-18px" :class="subjectTypeIcon('checkbox')"></i>
          多選題
        </b-button>
        <b-button class="subject-type-menu__create-button" variant="inverse-primary" @click="createNewSubject('select')">
          <i class="mdi mdi-18px" :class="subjectTypeIcon('select')"></i>
          下拉選單
        </b-button>
        <b-button class="subject-type-menu__create-button" variant="inverse-primary" @click="createNewSubject('datetime')">
          <i class="mdi mdi-18px" :class="subjectTypeIcon('datetime')"></i>
          日期與時間
        </b-button>
        <b-button class="subject-type-menu__create-button" variant="inverse-primary" @click="createNewSubject('uploader')" v-if="['booking', 'collection'].includes(formType)">
          <i class="mdi mdi-18px" :class="subjectTypeIcon('uploader')"></i>
          檔案上傳
        </b-button>
        <b-button class="subject-type-menu__create-button" variant="inverse-primary" @click="createNewSubject('collection_select')" v-if="formType === 'booking'">
          <i class="mdi mdi-18px" :class="subjectTypeIcon('collection_select')"></i>
          資料集關聯
        </b-button>
        <b-button class="subject-type-menu__create-button" variant="inverse-primary" @click="createNewSubject('collection_carousel')" v-if="formType === 'booking'">
          <i class="mdi mdi-18px" :class="subjectTypeIcon('collection_carousel')"></i>
          資料集開窗
        </b-button>
        <b-button class="subject-type-menu__create-button" variant="inverse-primary" @click="createNewSubject('shop_selection')" v-if="formType === 'booking'">
          <i class="mdi mdi-18px" :class="subjectTypeIcon('shop_selection')"></i>
          分店
        </b-button>
      </div>
    </div>

    <div v-if="!isReadOnly" class="mt-3 d-flex justify-content-center">
      <b-button variant="outline-danger" class="mr-3" @click="$emit('cancel')">
        返回
      </b-button>
      <b-button variant="success" @click="outputSubjects">
        儲存
      </b-button>
    </div>

    <!-- 顯示匯出的 JSON Modal Start -->
    <b-modal
      id="export-subjects-json"
      title="題目 JSON 輸出"
      cancel-title="取消"
    >
      <b-form-textarea v-model="exportSubjectsJson"></b-form-textarea>
    </b-modal>
    <!-- 顯示匯出的 JSON Modal End -->

    <!-- 匯入 JSON 的地方 Modal Start -->
    <b-modal
      id="import-subjects-json"
      title="匯入題目 JSON"
      cancel-title="取消"
      ok-title="匯入"
      @ok="saveImportSubjects"
    >
      <b-form-textarea v-model="importSubjectsJson" rows="10"></b-form-textarea>
      <p>一但匯入，將會將目前題目全數以匯入的 JSON 覆蓋，請確認</p>
      <p>本編輯器可支援 member_register.member_fields, survey_subjects</p>
    </b-modal>
    <!-- 匯入 JSON 的地方 Modal End -->
  </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid'
import VueDraggable from "vuedraggable";
import SubjectLayout from './SubjectLayout.vue';
import collectionApi from "@/apis/collection";
import DynamicFormDatePicker from '@/components/DynamicForm/DatePicker.vue';
import DynamicFormDateTimePicker from '@/components/DynamicForm/DateTimePicker.vue';
import DynamicFormTimePicker from '@/components/DynamicForm/TimePicker.vue';
import AppDatePicker from '@/components/AppDatePicker.vue';
import AppFileUploader from '@/components/AppFileUploader.vue';
import AppTimePicker from '@/components/AppTimePicker.vue';
import CarouselSelection from "@/components/DynamicForm/CarouselSelection";
import BranchProviderEditor from '@/components/Dashboard/Branch/BranchProviderEditor.vue';
import BranchSubsetEditor from '@/components/Dashboard/Branch/BranchSubsetEditor.vue';
import ShopSelection from "@/components/Page/Liff/Branch/ShopSelection";
import ShopSubsetSelection from "@/components/Page/Liff/Branch/ShopSubsetSelection";
import Collection from "@/models/Collection.model.js";
import DynamicFormField from "@/models/DynamicFormField.model"
import DynamicFormValuePresenter from '@/presenters/DynamicFormValue.presenter'
import { subjectTypeIcon } from '@/utils/DynamicFormUtils';
import deepGet from "lodash/get";

export default {
  components: {
    VueDraggable,
    SubjectLayout,
    DynamicFormDatePicker,
    DynamicFormDateTimePicker,
    DynamicFormTimePicker,
    AppDatePicker,
    AppFileUploader,
    AppTimePicker,
    CarouselSelection,
    BranchProviderEditor,
    BranchSubsetEditor,
    ShopSelection,
    ShopSubsetSelection,
  },
  props: {
    editorTitle: {
      type: String,
      default: '動態表單編輯器',
    },
    inputSubjects: {
      type: Array,
      default: () => { return [] },
    },
    formType: {
      type: String,
      default: 'survey',
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    pages: {
      type: Array,
      default: () => ([]),
    },
    sections: {
      type: Array,
      default: () => ([]),
    },
    validateSubject: {
      type: Boolean,
      default: false,
    },
    validationMessage: {
      type: String,
      default: '',
    },
    subjectIsFixed: {
      type: Function,
      default: (subject) => { return false }, // eslint-disable-line no-unused-vars
    },
    bookingPresetType: {
      type: String,
      default: 'normal',
    },
    startAtFieldId: {
      type: String,
      default: null,
    },
  },
  computed: {
    isReadOnly() {
      return this.readonly;
    },
    branchFieldOptions() {
      return [
        { text: '不設定', value: null },
        ...this.inputSubjects
          .filter(field => field.type === 'shop_selection')
          .map(field => ({ text: field.title, value: field._id })),
      ]
    },
  },
  watch: {
    inputSubjects: {
      handler: function (val) {
        for (let i = 0; i < val.length; i ++) {
          val[i] = (new DynamicFormField(val[i])).flattenedAttributes();

          // provide fallback values
          if (val[i].type === 'shop_selection') {
            this.$set(val[i], 'branch_population', deepGet(val[i], 'branch_population') || 'all');
            this.$set(val[i], 'branch_ids', deepGet(val[i], 'branch_ids') || []);
            this.$set(val[i], 'show_parent_branch_options', deepGet(val[i], 'show_parent_branch_options') || false);
            this.$set(val[i], 'parent_branch_population', deepGet(val[i], 'parent_branch_population') || 'parent');
            this.$set(val[i], 'parent_branch_provider', deepGet(val[i], 'parent_branch_provider') || null);
          }
          if (val[i].type === 'collection_select') {
            this.$set(val[i], 'collection_presentation', deepGet(val[i], 'collection_presentation') || 'select')
          }
        }

        function sortByKeyAsc(array, key) {
          return array.sort(function(a, b) {
              var x = a[key]; var y = b[key];
              return ((x < y) ? -1 : ((x > y) ? 1 : 0));
          });
        }

        this.newSubjects = sortByKeyAsc([...val], 'order'); // NOTE: using ... operator to avoid call-by-reference

        this.fetchFieldOptionsForCollectionCarousels();

        this.fetchBranchesForShopSelections();
      },
      immediate: true,
    },
  },
  data() {
    return {
      newSubjects: [],
      expandedStateForSubjects: {},
      exportSubjectsJson: null,
      importSubjectsJson: null,
      rerenderingCount: 0,
      collectionOptions: [],
      collectionFieldOptions: {},
      isFetchCollectionFields: {},
      previewCollectionItemOptions: {},
      isTyping: false,
    }
  },
  mounted() {
    this.fetchCollections();
  },
  methods: {
    deepGet,
    subjectTypeIcon,
    createNewSubject(newSubjectType) {
      let subject = {};

      switch (newSubjectType) {
        case 'textarea':
          subject.rows = 4;
          break;
        case "select":
          subject.enable_option_placeholder = false;
          subject.option_placeholder = '請選擇下方選項';
          subject.options = [
            {
              text: "",
              value: "",
            }
          ];
          break;
        case "radio":
        case "checkbox":
          subject.options = [
            {
              text: "",
              value: "",
            }
          ];
          break;
        case "datetime":
          subject.format = 'datetime';
          subject.time_condition = 'unlimited';
          subject.date_condition = 'unlimited';
          subject.start_time = '10:00';
          subject.end_time = '21:00';
          subject.slot_minutes = 30;
          subject.options = [
            {
              text: "",
              value: "",
            }
          ];
          subject.time_style = 'time_select_style';
          subject.date_style = 'calendar_style';
          subject.daily_time = 'daily_fixed_time';          
          break;
        case "collection_select":
          subject.collection_id = null;
          break;
        case "collection_carousel":
          subject.collection_id = null;
          subject.collection_title = null;
          subject.collection_image = null;
          subject.collection_description = null;
          break;
        case "shop_selection":
          subject.branch_population = 'all';
          subject.branch_ids = [];
          subject.show_parent_branch_options = false;
          subject.parent_branch_population = 'parent';
          subject.parent_branch_provider = null;
          break;
      }

      subject._id = uuidv4();
      subject.type = newSubjectType;
      subject.not_repeatable = false;

      this.newSubjects.push(subject);
      this.initExpandedStateForNewSubject(subject)
      this.focusSubjectTitleInput(subject._id);
    },
    focusSubjectTitleInput(subjectId) {
      this.$nextTick(() => {
        this.$refs[`subjectLayoutRef+${subjectId}`][0].$refs.titleInputRef.focus();
      });
    },
    initExpandedStateForNewSubject(subject) {
      this.expandedStateForSubjects[subject._id] = true;
    },
    removeNewSubject(index) {
      this.newSubjects.splice(index, 1);

      this.rerenderingCount++;
    },
    createOption(optionIndex, id) {
      const subject = this.newSubjects.find((s) => s._id === id)
      const clickedSubjectValue = subject.options[optionIndex].value;
      const nextOptionIndex = optionIndex + 1
      const isDateLimited =
        subject.type === 'datetime' &&
        subject.date_condition === 'limited' &&
        String(subject.format || '').includes('date')

      const isDailyFlexibleTime =  subject.type === 'datetime' &&  subject.daily_time === 'daily_flexible_time';

      subject.options.splice(nextOptionIndex, 0, {
        text: isDateLimited ? '' : clickedSubjectValue,
        value: clickedSubjectValue,
        ...(isDailyFlexibleTime ? { time_options: [{ text: '', value: '' }] } : {}),
      });

      this.rerenderingCount++;

      // this.$nextTick(() => {
      //   this.$refs[`${id}_${nextOptionIndex}`][0].select();
      // });
    },
    isOnlyOneOption(subjectIndex) {
      return this.newSubjects[subjectIndex].options.length === 1;
    },
    removeOption(subjectIndex, optionIndex) {
      if(this.isOnlyOneOption(subjectIndex)) return;
      this.newSubjects[subjectIndex].options.splice(optionIndex, 1);

      this.rerenderingCount++;
    },
    previewOptions(subjectIndex) {
      const options = deepGet(this.newSubjects[subjectIndex], 'options', [])
        .slice()
        .map(option => ({
          ...option,
          text: option.staff_only ? `${option.text} (前台隱藏)` : option.text,
          disabled: option.staff_only,
        }))

      if (this.newSubjects[subjectIndex].placeholder) {
        options.unshift({
          text: this.newSubjects[subjectIndex].placeholder,
          value: null,
          disabled: true,
        });
      }

      return options.filter(option => false === ["", null, undefined].includes(option.text))
    },
    syncOptionValue(subjectIndex, optionIndex) {
      this.newSubjects[subjectIndex].options[optionIndex].value = this.newSubjects[subjectIndex].options[optionIndex].text;
    },
    handleKeyDown({subjectIndex, optionIndex, id}) {
      if(this.isTyping) return;
      this.syncOptionValue(subjectIndex, optionIndex)
      this.createOption(optionIndex, id)
      this.$nextTick(() => {
        this.$refs[`${id}_${optionIndex + 1}`][0].select();
      });
    },
    compositionStart() {
      this.isTyping = true;
    },
    compositionEnd(subjectIndex, optionIndex){
      this.isTyping = false
      this.syncOptionValue(subjectIndex, optionIndex)
    },
    handlePaste(event, subjectIndex, optionIndex){
      const pasteData = event.clipboardData;
      const pastedText = pasteData.getData('text');

      if (pastedText.includes('\n')) {
        const subject = this.newSubjects[subjectIndex]
        const isDateLimited =
          subject.type === 'datetime' &&
          subject.date_condition === 'limited' &&
          String(subject.format || '').includes('date')
        const newOptions = pastedText.split('\n').map((text) => {
          const t = text.trim()
          return isDateLimited
            ? { text: '', value: t }
            : { text: t, value: t }
        });
        this.newSubjects[subjectIndex].options.splice(optionIndex + 1, 0, ...newOptions);
        this.newSubjects[subjectIndex].options.splice(optionIndex, 1);

        this.rerenderingCount++;

        // 由於此元件採用 rerendering 做 forceUpdate，使用 nextTick 無法完全確保元素已被渲染
        setTimeout(() => {
          this.$refs[`${this.newSubjects[subjectIndex]._id}_${optionIndex + newOptions.length - 1}`][0].select();
        }, 0);

      } else {
        this.newSubjects[subjectIndex].options[optionIndex].value = pastedText;
      }
    },
    outputSubjects() {
      if (false === this.validate()) {
        return
      }

      const outputSubjects = this.newSubjects.map((subject, index) => {
        // 過濾掉空的 time_options
        if (subject.time_options) {
          subject.time_options = subject.time_options.filter(option =>
            option.text !== '' || option.value !== ''
          )
        }

        return {
          ...subject,
          order: index + 1,
        }
      });

      this.$emit('output', outputSubjects);
    },
    exportSubjects() {
      this.exportSubjectsJson = JSON.stringify(this.newSubjects);
      this.$bvModal.show('export-subjects-json');
    },
    importSubjects() {
      this.$bvModal.show('import-subjects-json');
    },
    saveImportSubjects() {
      let subjects = JSON.parse(this.importSubjectsJson);
      this.newSubjects = subjects;
    },
    moveToTop(index) {
      if (index === 0) return;

      const subjectToMove = this.newSubjects[index];
      for (let i = index; i > 0; i--) {
        this.newSubjects[i] = this.newSubjects[i - 1];
      }
      this.newSubjects[0] = subjectToMove;

      this.rerenderingCount++;
    },
    moveUp(index) {
      if (index === 0) return;

      let temp = this.newSubjects[index];
      this.newSubjects[index] = this.newSubjects[index - 1];
      this.newSubjects[index - 1] = temp;

      this.rerenderingCount++;
    },
    moveDown(index) {
      if (index === this.newSubjects.length - 1) return;

      let temp = this.newSubjects[index];
      this.newSubjects[index] = this.newSubjects[index + 1];
      this.newSubjects[index + 1] = temp;

      this.rerenderingCount++;
    },
    moveToBottom(index) {
      if (index === this.newSubjects.length - 1) return;

      const subjectToMove = this.newSubjects[index];
      for (let i = index; i < this.newSubjects.length - 1; i++) {
        this.newSubjects[i] = this.newSubjects[i + 1];
      }
      this.newSubjects[this.newSubjects.length - 1] = subjectToMove;

      this.rerenderingCount++;
    },
    async fetchCollections() {
      try {
        let response = await collectionApi.getCollections({
          with_items: 1,
          is_enabled: 1,
        });
        this.collectionOptions = response.data.data.map((collection) => {
          return {
            value: collection.id,
            text: collection.name + ' 共 ' + collection.items_count + ' 筆資料',
            items: collection.items,
            config: collection.config,
          };
        });

        // 如果 subjects 中，有任何 collection_select，則要先提前將 previewCollectionItemOptions 設定好
        for (let i = 0; i < this.newSubjects.length; i++) {
          if (this.newSubjects[i].type === 'collection_select') {
            this.handleCollectionSelectChange(this.newSubjects[i].collection_id, this.newSubjects[i]._id);
          }
        }
      } catch (error) {
        console.error(error);
      }
    },
    async fetchCollectionItemForCarousel(subject) { // NOTE: 若 DynamicForm 與 DynamicFormDashboard 需要合併，則將此段邏輯改以 vue provide/inject 方式實作
      if ([null, undefined].includes(subject.collection_id)) {
        return []
      }

      try {
        const collection = deepGet(await collectionApi.getCollection(subject.collection_id), 'data.data', null)
        const collectionModel = new Collection(collection)

        const getValueFromCollectionItemByFieldId = (collectionItem, fieldId) => {
          if (fieldId === 'default') {
            return collectionModel.textForOption(collectionItem.id)
          }
          return deepGet(collectionItem.data.find(field => field.field_id === fieldId), 'value', null)
        }

        return collection.items.map((item) => ({
          id: item.id,
          title: getValueFromCollectionItemByFieldId(item, subject[`collection_title`]),
          image: getValueFromCollectionItemByFieldId(item, subject[`collection_image`]),
          description: getValueFromCollectionItemByFieldId(item, subject[`collection_description`]),
          value: DynamicFormValuePresenter.collectionItem(subject, item, collection),
          placeholderWhenSelected: collectionModel.textForOption(item.id),
        }))
      } catch (error) {
        console.error(error);
        this.$swal("錯誤", `讀取 ${subject.title} 選項時發生錯誤，請重新整理後再試一次`, "error")
        return []
      }
    },
    async handleCollectionSelectChange(value, subjectId) {
      const collection = this.collectionOptions.find((collection) => collection.value === value);

      if (! collection) {
        return;
      }

      const collectionModel = new Collection(collection)

      this.$set(
        this.previewCollectionItemOptions,
        subjectId,
        collection.items.map((item) => ({
          value: item.id,
          text: collectionModel.textForOption(item.id),
        }))
      )
    },
    async fetchFieldOptionsForCollectionCarousels() {
      return await Promise.all(
        this.inputSubjects
          .filter(subject => subject.type === 'collection_carousel')
          .map(subject => this.fetchFieldOptionsByCollectionId(deepGet(subject, 'collection_id')))
      )
    },
    async fetchFieldOptionsByCollectionId(collectionId) {
      if ([null, undefined].includes(collectionId)) {
        return
      }

      this.isFetchCollectionFields[collectionId] = true

      const res = await collectionApi.getCollection(collectionId);

      this.$set(
        this.collectionFieldOptions,
        collectionId,
        [
          { text: '預設顯示文字', value: 'default' },
          ...deepGet(res, 'data.data.config.fields', [])
            .map(field => ({
              text: deepGet(field, 'title') ?? deepGet('config.title'),
              value: deepGet(field, '_id') ?? deepGet(field, 'config._id'),
            })),
        ]
      )

      this.isFetchCollectionFields[collectionId] = false
    },
    async handleCollectionCarouselChange(collectionId, subject) {
      subject.collection_title = null
      subject.collection_image = null
      subject.collection_description = null
      await this.fetchFieldOptionsByCollectionId(collectionId)
      await this.updateCollectionCarouselPreview(subject._id)
    },
    async handleCollectionCarouselFieldMappingChange(mappingFieldId, subject) {
      await this.updateCollectionCarouselPreview(subject._id)
    },
    async updateCollectionCarouselPreview(subjectId) {
      await this.$refs[`carousel-preview-${subjectId}`][0].fetchCandidates()
    },
    async fetchBranchesForShopSelections() {
      this.$nextTick(async () => {
        return await Promise.all(
          this.inputSubjects
            .filter(subject => subject.type === 'shop_selection')
            .map(subject => {
              if (deepGet(subject, 'branch_population', 'all') === 'all' && deepGet(subject, 'branch_provider')) {
                this.$refs[`shop-selection-${subject._id}`][0].fetchBranchesOptions()
                this.$refs[`branch-provider-editor-${subject._id}`][0].loadSelectedBranch()
              }
              if (deepGet(subject, 'branch_population', 'all') === 'subset' && deepGet(subject, 'branch_ids.length', 0) > 0) {
                this.$refs[`shop-subset-selection-${subject._id}`][0].fetchBranchesOptions()
                this.loadSelectedParentBranch(subject)
              }
            })
        )
      })
    },
    handleShopSelectionProviderChange(subject) {
      this.$nextTick(() => {
        if (deepGet(subject, 'branch_population', 'all') === 'all' && deepGet(subject, 'branch_provider')) {
          this.$refs[`shop-selection-${subject._id}`][0].fetchBranchesOptions()
          this.$refs[`branch-provider-editor-${subject._id}`][0].loadSelectedBranch()
        }
        if (deepGet(subject, 'branch_population', 'all') === 'subset' && deepGet(subject, 'branch_ids.length', 0) > 0) {
          this.$refs[`shop-subset-selection-${subject._id}`][0].fetchBranchesOptions()
          this.loadSelectedParentBranch(subject)
        }
      })
    },
    handleShopSelectionPopulationChange(subject) {
      this.$set(subject, 'branch_ids', deepGet(subject, 'branch_ids') || [])
      this.$set(subject, 'parent_branch_population', deepGet(subject, 'parent_branch_population') || 'parent')
      this.$set(subject, 'parent_branch_provider', deepGet(subject, 'parent_branch_provider') || null)
      this.$nextTick(() => {
        if (deepGet(subject, 'branch_population', 'all') === 'all' && deepGet(subject, 'branch_provider')) {
          this.$refs[`shop-selection-${subject._id}`][0].fetchBranchesOptions()
          this.$refs[`branch-provider-editor-${subject._id}`][0].loadSelectedBranch()
        }
        if (deepGet(subject, 'branch_population', 'all') === 'subset' && deepGet(subject, 'branch_ids.length', 0) > 0) {
          this.$refs[`shop-subset-selection-${subject._id}`][0].fetchBranchesOptions()
          this.loadSelectedParentBranch(subject)
        }
      })
    },
    handleShowParentBranchesChange(value, subject) {
      if (value && !deepGet(subject, 'parent_branch_population')) {
        this.$set(subject, 'parent_branch_population', 'parent')
      }
      this.$nextTick(() => {
        this.refreshShopSubsetSelectionPreview(subject)
        this.loadSelectedParentBranch(subject)
      })
    },
    handleParentBranchPopulationChange(subject) {
      this.$nextTick(() => {
        this.refreshShopSubsetSelectionPreview(subject)
        this.loadSelectedParentBranch(subject)
      })
    },
    handleParentBranchProviderChange(subject) {
      this.$nextTick(() => {
        this.refreshShopSubsetSelectionPreview(subject)
        this.loadSelectedParentBranch(subject)
      })
    },
    handleBranchSubsetChange(subject) {
      this.$nextTick(() => {
        this.refreshShopSubsetSelectionPreview(subject)
      })
    },
    parentBranchProviderIsEnabled(subject) {
      return deepGet(subject, 'show_parent_branch_options') &&
        deepGet(subject, 'parent_branch_population', 'parent') === 'specific'
    },
    parentBranchProviderHasSpecificBranch(subject) {
      return !!deepGet((deepGet(subject, 'parent_branch_provider') || '').split(':'), 1)
    },
    refreshShopSubsetSelectionPreview(subject) {
      if (deepGet(subject, 'branch_population', 'all') !== 'subset') {
        return
      }
      if (deepGet(subject, 'branch_ids.length', 0) === 0) {
        return
      }
      this.$refs[`shop-subset-selection-${subject._id}`]?.[0]?.fetchBranchesOptions()
    },
    loadSelectedParentBranch(subject) {
      if (deepGet(subject, 'parent_branch_population', 'parent') !== 'specific') {
        return
      }
      if (!deepGet(subject, 'parent_branch_provider')) {
        return
      }
      this.$nextTick(() => {
        this.$refs[`parent-branch-provider-editor-${subject._id}`]?.[0]?.loadSelectedBranch()
      })
    },
    changeDateCondition(dateCondition, subjectId) {
      if (dateCondition == 'limited') {
        if (deepGet(this.newSubjects[subjectId], 'options.length', 0) === 0) {
          this.newSubjects[subjectId].options = [{ text:'', value:''}]
        }
        this.newSubjects[subjectId].date_style = "select_style"
      } else {
        this.newSubjects[subjectId].date_style = "calendar_style"
      }
      this.rerenderingCount++;
    },
    isInclude(word, value) {
      return value.includes(word)
    },
    handleTimeConditionChange(timeCondition, index) {
      if (timeCondition === 'unlimited') {
        this.newSubjects[index].time_style = 'time_select_style'
        this.newSubjects[index].daily_time = 'daily_fixed_time'
      } else if (timeCondition === 'limited') {
        this.newSubjects[index].time_style = 'select_style'
        this.newSubjects[index].daily_time = 'daily_fixed_time'
      } else if (timeCondition === 'specified') {
        this.newSubjects[index].time_style = 'select_style'
        if (!this.newSubjects[index].time_options || this.newSubjects[index].time_options.length === 0) {
          this.$set(this.newSubjects[index], 'time_options', [{ text: '', value: '' }])
        }
      }
      this.rerenderingCount++
    },

    handleDailyTimeChange(dailyTime, index) {
       if (dailyTime === 'daily_flexible_time') {
         this.newSubjects[index].options.forEach(option => {
          if (!option.time_options || option.time_options.length === 0) {
            this.$set(option, 'time_options', [{ text: '', value: '' }])
          }
         })
      }
      this.rerenderingCount++
    },

    createTimeOption(subjectIndex, optionIndex) {
      this.newSubjects[subjectIndex].time_options.splice(optionIndex + 1, 0, {
        text: '',
        value: '',
      })
      this.rerenderingCount++
    },

    createDailyTimeOption(subjectIndex, optionIndex, timeOptionIndex) {
      this.newSubjects[subjectIndex].options[optionIndex].time_options.splice(timeOptionIndex + 1, 0, {
        text: '',
        value: '',
      })
      this.rerenderingCount++
    },

    removeDailyTimeOption(subjectIndex, optionIndex, timeOptionIndex) {
      if (this.newSubjects[subjectIndex].options[optionIndex].time_options.length === 1) return
      this.newSubjects[subjectIndex].options[optionIndex].time_options.splice(timeOptionIndex, 1)
      this.rerenderingCount++
    },

    removeTimeOption(subjectIndex, optionIndex) {
      if (this.newSubjects[subjectIndex].time_options.length === 1) return
      this.newSubjects[subjectIndex].time_options.splice(optionIndex, 1)
      this.rerenderingCount++
    },
    validateSingleSubject(subject,idx) {
      if (subject.type === 'shop_selection' && subject.branch_population === 'subset' && deepGet(subject.branch_ids, 'length', 0) === 0) {
        this.$set(this.expandedStateForSubjects, subject._id, true)
        throw new Error(`欄位「${subject.title}」，尚未選擇任何分店`)
      }

      if (
        subject.type === 'shop_selection' &&
        subject.branch_population === 'subset' &&
        subject.show_parent_branch_options &&
        deepGet(subject, 'parent_branch_population', 'parent') === 'specific' &&
        !this.parentBranchProviderHasSpecificBranch(subject)
      ) {
        this.$set(this.expandedStateForSubjects, subject._id, true)
        throw new Error(`欄位「${subject.title || `第${idx+1}題`}」，尚未選擇上層分店來源`)
      }

      if (subject.type === 'shop_selection' && subject.branch_population === 'all' && [null, undefined, ''].includes(subject.branch_provider)) {
        this.$set(this.expandedStateForSubjects, subject._id, true)
        throw new Error(`欄位「${subject.title || `第${idx+1}題`}」，尚未選擇分店來源`)
      }

      if (subject.type === 'datetime' && subject.time_condition === 'specified' && subject.daily_time !== 'daily_flexible_time') {
        const hasEmptyTimeOptions = deepGet(subject, 'time_options', []).some(option =>
          (option.text === '' || option.text === null || option.text === undefined) &&
          (option.value === '' || option.value === null || option.value === undefined)
        ) 
        if (hasEmptyTimeOptions) {
          this.$set(this.expandedStateForSubjects, subject._id, true)
          throw new Error(`欄位「${subject.title || `第${idx+1}題`}」，特定時間選項不可為空，請填寫完整或刪除空白選項`)
        }
      }

      if (
        subject.type === 'datetime' &&
        subject.date_condition === 'limited' &&
        String(subject.format || '').includes('date')
      ) {
        const hasEmptyDateOptions = deepGet(subject, 'options', []).some((option) =>
          [null, undefined, ''].includes(option.value)
        )
        if (hasEmptyDateOptions) {
          this.$set(this.expandedStateForSubjects, subject._id, true)
          throw new Error(`欄位「${subject.title || `第${idx+1}題`}」，特定日期選項須設定完整日期，請填寫或刪除空白列`)
        }
      }
    },
    validate() {
      return this.newSubjects.every((subject,idx) => {
        try {
          this.validateSingleSubject(subject,idx)
          return true
        } catch (e) {
          this.$swal({
            type: 'error',
            title: '驗證失敗',
            text: e.message,
            onAfterClose: () => {
              const element = document.getElementById(`subject-${subject._id}`)
              element.scrollIntoView({ behavior: "smooth" })
            },
          })
          console.warn("[DynamicFormEditor] Validation failed.");
          return false
        }
      })
    },
    handleFieldChange(subject, field) {

      subject.daily_time = 'daily_fixed_time'

      this.$emit('change', subject, field)
    },
  }
}
</script>

<style lang="scss" scoped>
.form-group ::v-deep .form-row {
  .col {
    display: flex;
    align-items: center;
  }
}

.form-group-radio,
.form-group-checkbox {
  ::v-deep {
    .col {
      flex-direction: column;
      align-items: start;
    }

    .input-group {
      flex-direction: column;
      align-items: start;
    }
  }
}

.subject-type-menu {
  display: inline-flex;
  align-items: center;
  border: 1px dashed #aaa;
  border-radius: 12px;
  padding: 4px 8px;

  &__icon {
    flex: 0 0 auto;
    margin: 0 8px;
  }

  &__button-wrapper {
    flex: 1 1 auto;
    flex-wrap: wrap;

    > * {
      margin: 4px 8px;
    }
  }

  &__create-button {
    display: inline-flex;
    align-items: center;
    text-wrap: nowrap;

    i.mdi {
      margin-right: 4px;
    }
  }
}

.option-list-header {
  display: flex;
  align-items: center;
}

.shop-selection-parent-branch-options {
  color: #555;

  &--disabled {
    color: #888;
  }
}

.shop-selection-parent-branch-provider {
  display: inline-flex;
  align-items: center;

  &--disabled {
    ::v-deep .branch-provider-selection__open-modal-button {
      pointer-events: none;
      opacity: .65;
    }
  }
}

.vue__time-picker{
  width: auto !important;
  max-width: 170px;
}
</style>
